<template>
  <div class="service-area-map-container" ref="mapContainer" :data-active-tool="activeTool">

    <!-- Toolbar positioned to work in both normal and fullscreen modes -->
    <geometry-edit-toolbar :active-tool="activeTool" :can-undo="canUndo" :can-redo="canRedo" :is-dirty="isDirty"
      :delete-target="selectedPartIndex !== null ? 'part' : 'sector'"
      :class="{ 'toolbar-fullscreen': isFullscreen }" @tool-change="handleToolChange" @undo="handleUndo"
      @redo="handleRedo" @save="handleSave" @cancel="handleCancel" @deselect="clearSelection" />

    <l-map :style="`height: ${isFullscreen ? '100%' : height}; width: ${isFullscreen ? '100%' : width}`"
      :center="mapCenter" :zoom="zoom" :options="leafletOptions" @ready="handleMapReady" ref="serviceAreaMap">
      <!-- Base map layer -->
      <l-tile-layer v-bind="basemap" />

      <!-- Existing zoneparts (non-editable background) -->
      <l-geo-json v-for="zonepart in backgroundZoneparts" v-if="zonepart.geometry && isValidGeometry(zonepart.geometry)"
        :key="`bg-layer-${zonepart.id}-${zonepart.zone_id}`"
        :geojson="getCleanGeometryForLayer(zonepart) || zonepart.geometry" :options="backgroundOptions"
        :options-style="getBackgroundStyle(zonepart)" class="zonepart-layer"
        @ready="handleBackgroundLayerReady($event, zonepart)" />

      <!-- Editable zoneparts -->
      <l-geo-json v-for="zonepart in editableZoneparts" v-if="zonepart.geometry && isValidGeometry(zonepart.geometry)"
        :key="`edit-layer-${zonepart.id}-${zonepart.zone_id}`"
        :geojson="getCleanGeometryForLayer(zonepart) || zonepart.geometry" :options="editableOptions"
        :options-style="getEditableStyle(zonepart)" class="zonepart-layer"
        @ready="handleEditableLayerReady($event, zonepart)" />

      <!-- Zone labels -->
      <l-marker v-for="label in zoneLabels" :key="`label-${label.id}`" :lat-lng="label.center" :icon="label.icon" />

      <!-- Zonepart/Sector labels -->
      <l-marker v-for="label in zonepartLabels" :key="`zonepart-label-${label.id}`" :lat-lng="label.center"
        :icon="label.icon" />

      <!-- Location pins -->
      <l-feature-group v-if="showLocationPins">
        <l-marker v-for="location in locationMarkers" :key="`location-${location.id}`" :lat-lng="location.position"
          :icon="location.icon" :z-index-offset="500">
          <l-tooltip :options="{ permanent: false, direction: 'top' }">
            <div class="location-tooltip">
              <strong>{{ location.name }}</strong>
              <div v-if="location.address && location.address.street">{{ location.address.street }}</div>
              <div v-if="location.address && location.address.city">
                {{ location.address.city }}, {{ location.address.state }} {{ location.address.zip }}
              </div>
            </div>
          </l-tooltip>
        </l-marker>
      </l-feature-group>

      <!-- Drawing layer (for temporary shapes) -->
      <l-feature-group ref="drawingLayer" />

      <!-- Map controls -->

      <!-- Selected Zonepart Info Panel -->
      <l-control v-if="selectedFeature && selectedFeature.type === 'zonepart'" position="topright"
        class="selected-zonepart-control">
        <div class="selected-zonepart-panel">
          <div class="panel-header">
            <i class="fa fa-map-marker"></i>
            <span>Selected Sector</span>
          </div>
          <div class="panel-content">
            <div class="info-row">
              <label>Sector:</label>
              <span class="info-value">{{ selectedFeature.data?.name || `Sector ${selectedFeature.id}` }}</span>
            </div>
            <div class="info-row">
              <label>Zone:</label>
              <span class="info-value">{{ getZoneName(selectedFeature.data?.zone_id) }}</span>
            </div>
            <div v-if="selectedPartIndex !== null" class="info-row">
              <label>Part:</label>
              <span class="info-value">{{ selectedPartIndex + 1 }} of {{ totalParts }}</span>
            </div>
          </div>
        </div>
      </l-control>

      <!-- Zone/Sectors List Panel -->
      <l-control position="topleft" class="zone-list-control">
        <!-- Collapsed state - Icon only -->
        <div v-if="zoneListCollapsed" class="map-zone-list-icon" @click="toggleZoneList" title="Zones & Sectors">
          <i class="fa fa-list"></i>
          <span class="zone-count-badge">{{ zones.length }}</span>
        </div>

        <!-- Expanded state - Full panel -->
        <div v-else class="map-zone-list">
          <div class="zone-list-header" @click="toggleZoneList">
            <i class="fa fa-chevron-up"></i>
            <span>Zones & Sectors</span>
            <span class="zone-count">({{ zones.length }})</span>
          </div>
          <zone-parts-list
            :school-id="schoolId"
            :selected-part-index="selectedPartIndex"
            :on-feature-select="handleFeatureSelect"
            @edit-zonepart-name="handleEditZonepartName"
            @zoom-to="handleZoomToZonepart"
            @select-part="handleSelectPart"
            @delete-part="handleDeletePart"
            class="zone-list-content" />
        </div>
      </l-control>

      <!-- Location Pins Toggle Button -->
      <l-control position="topright">
        <button class="leaflet-control-button location-toggle-btn" @click="toggleLocationPins"
          :title="showLocationPins ? 'Hide Location Pins' : 'Show Location Pins'">
          <i class="fa" :class="showLocationPins ? 'fa-map-marker' : 'fa-map-o'"></i>
        </button>
      </l-control>

      <!-- Legend -->
      <l-control v-if="showLegend" position="bottomright">
        <div class="map-legend">
          <div class="legend-title">Zones</div>
          <div v-for="zone in zones" :key="zone.id" class="legend-item">
            <i class="zone-color" :style="`background-color: ${getZoneColor(zone)}`"></i>
            <span>{{ zone.name }}</span>
          </div>
        </div>
      </l-control>
    </l-map>

    <!-- Modal Container - stays within map container for fullscreen support -->
    <div class="map-modal-container">
      <!-- Unsaved Changes Dialog -->
      <unsaved-changes-dialog :show-dialog="showUnsavedChangesDialog" :saving="savingChanges"
        :is-fullscreen="isFullscreen" @discard="handleConfirmDiscard" @cancel="handleConfirmCancel" />

      <!-- New Sector Dialog -->
      <new-sector-dialog :show="showNewSectorDialog" :zones="zones" :is-fullscreen="isFullscreen"
        @confirm="handleNewSectorConfirm" @cancel="handleNewSectorCancel" />

      <!-- Edit Name Modal from ZonePartsList -->
      <div v-if="editingZonepartName" class="modal-overlay map-modal" @click="cancelEditName">
        <div class="edit-modal" @click.stop>
          <h4>Edit Name</h4>
          <input type="text" class="form-control" v-model="editingName" @keyup.enter="saveEditName"
            @keyup.esc="cancelEditName" ref="editNameInput">
          <div class="modal-actions">
            <button class="btn btn-primary btn-sm" @click="saveEditName">Save</button>
            <button class="btn btn-default btn-sm" @click="cancelEditName">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { LMap, LTileLayer, LGeoJson, LMarker, LControl, LFeatureGroup, LTooltip } from 'vue2-leaflet';
import L from 'leaflet';
import 'leaflet-editable';
import 'leaflet-path-transform'; // For native path dragging functionality
import 'leaflet-fullscreen';
import 'leaflet-fullscreen/dist/leaflet.fullscreen.css';
import GeometryEditToolbar from './GeometryEditToolbar.vue';
import ZonePartsList from './ZonePartsList.vue';
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex';
import GeometryService from './services/GeometryService';
import MapEventService from './services/MapEventService';
import NewSectorDialog from './NewSectorDialog.vue';
import UnsavedChangesDialog from './UnsavedChangesDialog.vue';
import { BASEMAP } from '../../../../DriveScout/Config.js';

export default {
  name: 'ServiceAreaMap',
  components: {
    LMap,
    LTileLayer,
    LGeoJson,
    LMarker,
    LControl,
    LFeatureGroup,
    LTooltip,
    GeometryEditToolbar,
    ZonePartsList,
    UnsavedChangesDialog,
    NewSectorDialog
  },
  props: {
    height: {
      type: String,
      default: '600px'
    },
    width: {
      type: String,
      default: '100%'
    },
    schoolId: {
      type: Number,
      required: true
    },
    showLegend: {
      type: Boolean,
      default: true
    },
    useNativePathDrag: {
      type: Boolean,
      default: false // Feature flag for new drag implementation
    }
  },
  data() {
    return {
      map: null,
      mapCenter: [33.7490, -84.3880], // Default to Atlanta, will be calculated in mounted
      initialCenterSet: false, // Track if initial centering has been done
      currentTool: 'select', // Default to select tool
      editHistory: [],
      historyIndex: -1,
      backgroundLayers: new Map(),
      editableLayers: new Map(),
      currentEditableLayer: null,
      currentDrawingLayer: null,
      layerReadyCount: 0,
      zoom: 12,
      showUnsavedChangesDialog: false,
      savingChanges: false,
      isFullscreen: false,
      isEditingVertex: false, // Flag to prevent deselection during vertex editing
      isMoving: false, // Flag to prevent layer recreation during move
      pendingGeometryUpdate: null, // Store pending geometry updates during vertex editing
      vertexEditSession: null, // Track active vertex edit session
      moveSession: null, // Track active move session
      currentMoveableLayer: null, // Store reference to moveable polygon
      pendingSelection: null, // Store pending selection when unsaved changes exist
      originalZoneparts: null, // Store original state for cancel/discard
      newSectorData: null, // Store new sector name and zone
      showNewSectorDialog: false, // Show new sector dialog
      zoneListCollapsed: false, // State for zone list panel collapse
      editingZonepartName: false, // Show edit name modal
      editingItem: null, // Item being edited
      editingName: '', // Name being edited
      selectedPartIndex: null, // For MultiPolygon sectors: index of the sub-selected part, or null when whole sector is selected
      partHighlightLayer: null, // Leaflet overlay for the sub-selected part (non-reactive)
      basemap: BASEMAP,
      leafletOptions: {
        zoomControl: true,
        fullscreenControl: true,
        editable: true // Enable Leaflet.Editable
      },
      backgroundOptions: {
        interactive: true  // Must be interactive to handle clicks
      },
      editableOptions: {
        interactive: true
      }
    };
  },
  computed: {
    ...mapState('serviceAreas', [
      'zones',
      'zoneparts',
      'selectedFeature',
      'activeTool',
      'isDirty'
    ]),
    ...mapGetters('serviceAreas', [
      'getZoneById',
      'getZonepartsByZoneId',
      'getCachedGeometry',
      'getCleanGeometry',
      'getZonepartsWithCleanGeometry'
    ]),
    backgroundZoneparts() {
      // Show all non-selected zoneparts as background
      // Ensure zoneparts is an array and has valid data
      const validZoneparts = Array.isArray(this.zoneparts) ? this.zoneparts : [];

      const result = validZoneparts.filter(zp =>
        zp && zp.id && (!this.selectedFeature || this.selectedFeature.id !== zp.id)
      );

      // Filter to only include zoneparts with valid geometry using GeometryService
      const withValidGeometry = result.filter(zp =>
        zp && zp.geometry && GeometryService.isValidGeometry(zp.geometry)
      );

      console.log('backgroundZoneparts computed:', {
        activeTool: this.activeTool,
        totalZoneparts: validZoneparts.length,
        backgroundCount: result.length,
        withValidGeometry: withValidGeometry.length,
        selectedFeature: this.selectedFeature?.id
      });

      return withValidGeometry;
    },
    editableZoneparts() {
      // CRITICAL: During vertex editing, return cached result to prevent layer recreation
      if (this.isEditingVertex && this.vertexEditSession) {
        console.log('Vertex editing in progress, returning cached editable zoneparts');
        return this.vertexEditSession.cachedEditableZoneparts || [];
      }

      // CRITICAL: During move operation, return cached result to prevent layer recreation
      if (this.isMoving && this.moveSession) {
        console.log('Move in progress, returning cached editable zoneparts');
        return this.moveSession.cachedEditableZoneparts || [];
      }

      // Show only the selected zonepart as editable (when a zonepart is selected)
      let result = [];

      // Ensure zoneparts is an array
      const validZoneparts = Array.isArray(this.zoneparts) ? this.zoneparts : [];

      if (this.selectedFeature?.type === 'zonepart' && this.selectedFeature.id) {
        result = validZoneparts.filter(zp =>
          zp && zp.id === this.selectedFeature.id &&
          zp.geometry && GeometryService.isValidGeometry(zp.geometry)
        );
      }

      console.log('editableZoneparts computed:', {
        activeTool: this.activeTool,
        selectedFeature: this.selectedFeature?.id,
        selectedType: this.selectedFeature?.type,
        editableCount: result.length,
        isEditingVertex: this.isEditingVertex
      });

      return result;
    },
    zoneLabels() {
      return this.zones.map(zone => {
        const zoneparts = this.getZonepartsByZoneId(zone.id);
        if (zoneparts.length === 0) return null;

        // Calculate zone center from its zoneparts
        let bounds = L.latLngBounds();
        zoneparts.forEach(zp => {
          if (zp.geometry) {
            const layer = L.geoJSON(zp.geometry);
            bounds.extend(layer.getBounds());
          }
        });

        const textColor = zone.text_color || '#333';

        return {
          id: zone.id,
          center: bounds.getCenter(),
          icon: L.divIcon({
            className: 'zone-label',
            html: `<span style="color:${textColor}">${zone.name}</span>`
          })
        };
      }).filter(Boolean);
    },
    zonepartLabels() {
      return this.zoneparts.map(zonepart => {
        if (!zonepart.geometry) return null;

        try {
          // Calculate the center of the zonepart geometry
          const layer = L.geoJSON(zonepart.geometry);
          const bounds = layer.getBounds();

          const parentZone = this.getZoneById(zonepart.zone_id);
          const textColor = (parentZone && parentZone.text_color) || '#555';

          return {
            id: zonepart.id,
            center: bounds.getCenter(),
            icon: L.divIcon({
              className: 'zonepart-label',
              html: `<span style="color:${textColor}">${zonepart.name || `Sector ${zonepart.id}`}</span>`,
              iconSize: [100, 20]
            })
          };
        } catch (error) {
          console.warn('Failed to create label for zonepart:', zonepart.id, error);
          return null;
        }
      }).filter(Boolean);
    },
    canUndo() {
      return this.historyIndex > 0;
    },
    canRedo() {
      return this.historyIndex < this.editHistory.length - 1;
    },
    selectedZonepart() {
      if (!this.selectedFeature || this.selectedFeature.type !== 'zonepart') return null;
      return this.zoneparts.find(zp => zp.id === this.selectedFeature.id) || null;
    },
    selectedIsMultiPolygon() {
      return this.selectedZonepart?.geometry?.type === 'MultiPolygon';
    },
    totalParts() {
      const geometry = this.selectedZonepart?.geometry;
      if (!geometry) return 0;
      if (geometry.type === 'MultiPolygon') return geometry.coordinates.length;
      if (geometry.type === 'Polygon') return 1;
      return 0;
    },
    showLocationPins() {
      // Get location pins visibility state from Vuex store
      return this.$store.getters['serviceAreas/showLocationPins'];
    },
    locationMarkers() {
      // Get school locations that have coordinates
      const locations = this.$store.getters['serviceAreas/schoolLocationsWithCoordinates'];

      // Transform locations to marker format
      return locations.map(location => ({
        id: location.id,
        position: [location.lat, location.lng],
        name: location.name,
        address: location.address || {},
        icon: this.locationPinIcon
      }));
    },
    locationPinIcon() {
      // Custom icon for location pins with visual distinction
      return L.icon({
        iconUrl: 'images/map-pin.png',  // Use string path for webpack-served images
        iconSize: [24, 39],
        iconAnchor: [12, 39], // Bottom center of the pin
        className: 'location-pin-marker' // CSS class for styling
      });
    }
  },
  watch: {
    // Sync local currentTool with Vuex activeTool
    activeTool: {
      handler(newTool) {
        if (newTool !== this.currentTool) {
          console.log('Syncing currentTool with activeTool from Vuex:', newTool);
          this.currentTool = newTool;
        }
      },
      immediate: true
    },
    selectedFeature: {
      handler(newSelection, oldSelection) {
        // Hide selection prompt when a feature is selected
        if (newSelection) {
        }

        // Skip processing during active vertex editing session
        if (this.isEditingVertex && this.vertexEditSession) {
          console.log('Vertex editing session active, skipping selection change processing');
          return;
        }

        console.log('Selected feature changed:', {
          old: oldSelection?.id,
          new: newSelection?.id,
          currentTool: this.currentTool,
          isEditingVertex: this.isEditingVertex
        });

        // Disable editing on the old selection only if it's different from new selection
        if (oldSelection && oldSelection.id && (!newSelection || oldSelection.id !== newSelection?.id)) {
          const oldLayer = this.editableLayers.get(oldSelection.id);
          if (oldLayer) {
            this.disableEditing(oldLayer);
          }
        }

        // Clear sub-selection (MultiPolygon part highlight) when the selected sector changes
        if (!newSelection || !oldSelection || newSelection.id !== oldSelection.id) {
          this.selectedPartIndex = null;
          this.removePartHighlight();
        }

        // Force re-rendering of layers by triggering reactive updates
        // This ensures opacity changes are applied immediately
        this.$nextTick(() => {
          // The computed properties will automatically recalculate and update styles
          this.updateLayerStyles();

          // If edit-shape tool is selected, enable editing for new selection
          if (this.currentTool === 'edit-shape' &&
            newSelection &&
            newSelection.type === 'zonepart' &&
            !this.isEditingVertex) { // Don't auto-enable during vertex editing
            const newLayer = this.editableLayers.get(newSelection.id);
            if (newLayer) {
              console.log('Auto-enabling editing for newly selected feature');
              this.enableEditing(newLayer);
            }
          }
        });
      },
      immediate: true
    },
    zoneparts: {
      immediate: true,
      handler(newVal) {
        // Only center on initial load, not on every update
        if (!this.initialCenterSet && newVal && newVal.length > 0 && this.map) {
          console.log('Zoneparts loaded, centering map on service area');
          this.centerMapOnZoneparts();
          this.initialCenterSet = true;
        }
      }
    }
  },
  methods: {
    ...mapActions('serviceAreas', [
      'fetchZones',
      'fetchZoneparts',
      'updateZonepart',
      'saveAllChanges',
      'saveSelectedZonepart',
      'createZonepart',
      'deleteZonepart',
      'selectFeature',
      'setActiveTool',
      'setDirty',
      'resetToCleanState',
      'setCleanState',
      'cacheGeometry',
      'clearGeometryCache',
      'removeCachedGeometry',
      'toggleLocationPins'
    ]),
    ...mapMutations('serviceAreas', [
      'UPDATE_ZONEPART'
    ]),
    updateZonepartLocally(zonepartId, data) {
      // Update local state without API call
      this.UPDATE_ZONEPART({ zonepartId, data });
      // Remove cached geometry since it changed
      this.removeCachedGeometry(zonepartId);
      // Mark as dirty
      this.setDirty(true);
    },
    isValidGeometry(geometry) {
      return GeometryService.isValidGeometry(geometry);
    },
    getCleanGeometryForLayer(zonepart) {
      // Use the clean geometry from the store if available
      const cleanGeometry = this.getCleanGeometry(zonepart.id);
      if (cleanGeometry) {
        return cleanGeometry;
      }

      // Otherwise try to clean it now
      try {
        return GeometryService.cleanGeometry(zonepart.geometry);
      } catch (error) {
        console.warn('Failed to clean geometry for zonepart:', zonepart.id, error);
        return null;
      }
    },
    getZoneColor(zone) {
      // Handle undefined zones gracefully
      if (!zone || !zone.id) {
        console.warn('getZoneColor called with undefined or invalid zone:', zone);
        return '#999999'; // Default gray color for undefined zones
      }

      if (zone.bg_color) {
        return zone.bg_color;
      }

      // Generate high-contrast colors using HSL for better distinction
      // Use golden ratio to maximize hue separation
      const goldenRatio = 0.618033988749895;
      const hue = ((zone.id * goldenRatio) % 1) * 360;

      // Use high saturation and medium lightness for vibrant, distinguishable colors
      // Alternate between two lightness levels for additional contrast
      const saturation = 75 + (zone.id % 2) * 10; // 75% or 85%
      const lightness = 45 + (zone.id % 3) * 5; // 45%, 50%, or 55%

      return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    },
    getBackgroundStyle(zonepart) {
      const zone = this.getZoneById(zonepart.zone_id);
      const baseColor = this.getZoneColor(zone);

      return {
        fillColor: baseColor,
        fillOpacity: 0.3,
        color: baseColor,
        weight: 2,
        opacity: 0.8
      };
    },
    getEditableStyle(zonepart) {
      const zone = this.getZoneById(zonepart.zone_id);
      const baseColor = this.getZoneColor(zone);
      const isSelected = this.selectedFeature && this.selectedFeature.id === zonepart.id;

      return {
        fillColor: isSelected ? '#ff0066' : baseColor,
        fillOpacity: isSelected ? 0.1 : 0.3,
        color: isSelected ? '#ff0066' : baseColor,
        weight: isSelected ? 3 : 2,
        opacity: 1,
        dashArray: isSelected ? '5,5' : null
      };
    },
    getEditingStyle(zonepart) {
      // Style for actively editing polygon
      return {
        fillColor: '#fe57a1',
        fillOpacity: 0.1,
        color: '#fe57a1',
        weight: 3,
        opacity: 1,
        dashArray: '5,5'
      };
    },
    handleMapReady(mapObject) {
      this.map = mapObject;

      // Ensure Leaflet.Editable is properly initialized
      if (!this.map.editTools) {
        console.warn('Leaflet.Editable not initialized, initializing now');
        // Initialize Leaflet.Editable if not already done
        if (L.Editable) {
          this.map.editTools = new L.Editable(this.map);
        } else {
          console.error('Leaflet.Editable not loaded!');
        }
      } else {
        console.log('Leaflet.Editable already initialized');
      }

      // Setup custom fullscreen handling that includes the toolbar
      this.setupFullscreenControl();

      // If we have zoneparts data and haven't centered yet, do it now
      if (!this.initialCenterSet && this.zoneparts && this.zoneparts.length > 0) {
        console.log('Map ready with zoneparts data, centering on service area');
        this.centerMapOnZoneparts();
        this.initialCenterSet = true;
      }

      // Add fullscreen event handlers
      this.map.on('enterFullscreen', () => {
        console.log('Map entered fullscreen mode');
        this.isFullscreen = true;
        // Add fullscreen class to container
        if (this.$refs.mapContainer) {
          this.$refs.mapContainer.classList.add('is-fullscreen');
        }
      });

      this.map.on('exitFullscreen', () => {
        console.log('Map exited fullscreen mode');
        this.isFullscreen = false;
        // Remove fullscreen class from container
        if (this.$refs.mapContainer) {
          this.$refs.mapContainer.classList.remove('is-fullscreen');
        }
      });

      // Add click handler for deselection when clicking on empty map areas
      // Note: With tool-based workflow, we don't auto-deselect on map click
      // Selection is controlled through the Select tool
      this.map.on('click', (e) => {
        // Map panning and other interactions work normally
        // Selection/deselection only happens through explicit tool actions
      });

      // Set up Leaflet.Editable event handlers
      this.map.on('editable:drawing:start', (e) => {
        console.log('Drawing started:', e);
      });

      this.map.on('editable:drawing:end', (e) => {
        console.log('Drawing ended:', e);
        this.handleDrawingComplete(e.layer);
      });

      this.map.on('editable:drawing:cancel', (e) => {
        console.log('Drawing cancelled:', e);
      });

      // Vertex editing events with proper session management
      this.map.on('editable:vertex:dragstart', (e) => {
        console.log('Vertex drag started:', e);
        if (this.map.dragging) {
          this.map.dragging.disable();
        }
        this.startVertexEditSession(e);
      });

      this.map.on('editable:vertex:dragend', (e) => {
        console.log('Vertex drag ended:', e);
        this.handleVertexEdit(e);
        if (this.map.dragging) {
          this.map.dragging.enable();
        }
      });

      this.map.on('editable:vertex:deleted', (e) => {
        console.log('Vertex deleted:', e);
        this.handleVertexEdit(e);
      });

      this.map.on('editable:vertex:new', (e) => {
        console.log('New vertex created:', e);
        this.handleVertexEdit(e);
      });
    },
    setupFullscreenControl() {
      // Check if fullscreen control exists
      if (!this.map || !this.map._controlContainer) return;

      // Find the fullscreen control
      const fullscreenControl = this.map._controlContainer.querySelector('.leaflet-control-fullscreen');
      if (!fullscreenControl) {
        console.log('Fullscreen control not found, might be added later');
        return;
      }

      // Override the fullscreen behavior to use our container
      const fullscreenButton = fullscreenControl.querySelector('a');
      if (fullscreenButton) {
        // Remove existing click handlers
        const newButton = fullscreenButton.cloneNode(true);
        fullscreenButton.parentNode.replaceChild(newButton, fullscreenButton);

        // Add our custom fullscreen handler
        newButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.toggleFullscreen();
        });
      }
    },
    toggleFullscreen() {
      const container = this.$refs.mapContainer;
      if (!container) return;

      if (!document.fullscreenElement && !document.webkitFullscreenElement &&
        !document.mozFullScreenElement && !document.msFullscreenElement) {
        // Enter fullscreen
        if (container.requestFullscreen) {
          container.requestFullscreen();
        } else if (container.webkitRequestFullscreen) {
          container.webkitRequestFullscreen();
        } else if (container.mozRequestFullScreen) {
          container.mozRequestFullScreen();
        } else if (container.msRequestFullscreen) {
          container.msRequestFullscreen();
        }

        this.isFullscreen = true;
        container.classList.add('is-fullscreen');
        this.map.fire('enterFullscreen');
      } else {
        // Exit fullscreen
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }

        this.isFullscreen = false;
        container.classList.remove('is-fullscreen');
        this.map.fire('exitFullscreen');
      }

      // Update map size after transition
      setTimeout(() => {
        if (this.map) {
          this.map.invalidateSize();
        }
      }, 300);
    },
    handleBackgroundLayerReady(layer, zonepart) {
      this.backgroundLayers.set(zonepart.id, layer);

      // Add click handler for selection
      layer.on('click', (e) => {
        console.log('Background layer clicked:', {
          zonepartId: zonepart.id,
          zonepartName: zonepart.name,
          activeTool: this.activeTool,
          selectedFeature: this.selectedFeature
        });
        L.DomEvent.stopPropagation(e);
        // Only allow selection when Select tool is active
        if (this.activeTool === 'select') {
          console.log('Select tool active, selecting zonepart:', zonepart.id);
          this.selectZonepart(zonepart);
        } else {
          console.log('Select tool not active, ignoring click. Current tool:', this.activeTool);
        }
      });

      // Add hover effects
      layer.on('mouseover', () => {
        if (!this.selectedFeature || this.selectedFeature.id !== zonepart.id) {
          layer.setStyle({
            weight: 3,
            opacity: 1
          });
        }
      });

      layer.on('mouseout', () => {
        if (!this.selectedFeature || this.selectedFeature.id !== zonepart.id) {
          layer.setStyle(this.getBackgroundStyle(zonepart));
        }
      });
    },
    /**
     * Renders or removes the magenta highlight overlay on the sub-selected part.
     */
    updatePartHighlight() {
      this.removePartHighlight();
      if (this.selectedPartIndex === null) return;
      const zonepart = this.selectedZonepart;
      if (!zonepart || zonepart.geometry?.type !== 'MultiPolygon') return;
      const polygonCoords = zonepart.geometry.coordinates[this.selectedPartIndex];
      if (!polygonCoords) return;

      // Convert GeoJSON polygon coords ([lng,lat]) to Leaflet latlng arrays
      const latlngs = polygonCoords.map(ring =>
        ring.map(coord => L.latLng(coord[1], coord[0]))
      );
      const overlay = L.polygon(latlngs, {
        fillColor: '#ff0066',
        fillOpacity: 0.25,
        color: '#ff0066',
        weight: 4,
        opacity: 1,
        dashArray: '6,4',
        interactive: false
      });
      overlay.addTo(this.map);
      overlay.bringToFront();
      this.partHighlightLayer = overlay;
    },
    removePartHighlight() {
      if (this.partHighlightLayer && this.map) {
        try {
          this.map.removeLayer(this.partHighlightLayer);
        } catch (err) {
          console.warn('Error removing part highlight layer:', err);
        }
      }
      this.partHighlightLayer = null;
    },
    handleEditableLayerReady(layer, zonepart) {
      console.log('Editable layer ready for zonepart:', zonepart.id);

      // During vertex editing, be very careful about layer management
      if (this.isEditingVertex && this.vertexEditSession && this.vertexEditSession.zonepartId === zonepart.id) {
        console.log('Layer ready during vertex edit session, preserving edit state');

        // Update the session's parent layer reference
        layer.zonepart = zonepart;
        this.vertexEditSession.parentLayer = layer;
        this.editableLayers.set(zonepart.id, layer);

        // Don't set up new handlers or enable editing - preserve existing state
        return;
      }

      // Clean up any existing editable layer for this zonepart
      const existingLayer = this.editableLayers.get(zonepart.id);
      if (existingLayer && existingLayer !== layer) {
        console.log('Removing existing editable layer for zonepart:', zonepart.id);
        try {
          // Disable editing and remove handlers
          this.disableEditing(existingLayer);
          existingLayer.off();
          if (this.map && this.map.hasLayer(existingLayer)) {
            this.map.removeLayer(existingLayer);
          }
        } catch (error) {
          console.warn('Error removing existing editable layer:', error);
        }
      }

      // Store reference to layer and zonepart data
      layer.zonepart = zonepart;
      this.editableLayers.set(zonepart.id, layer);

      // Add click handler
      layer.on('click', (e) => {
        console.log('Editable layer clicked:', {
          zonepartId: zonepart.id,
          zonepartName: zonepart.name,
          activeTool: this.activeTool,
          currentTool: this.currentTool,
          selectedFeature: this.selectedFeature,
          isAlreadySelected: this.selectedFeature && this.selectedFeature.id === zonepart.id
        });
        L.DomEvent.stopPropagation(e);

        // Only handle clicks when Select tool is active
        if (this.activeTool === 'select') {
          // Select the clicked zonepart if it's not already selected
          if (!this.selectedFeature || this.selectedFeature.id !== zonepart.id) {
            console.log('Select tool active, selecting zonepart:', zonepart.id);
            this.selectZonepart(zonepart);
          } else {
            console.log('Zonepart already selected:', zonepart.id);
          }
        } else {
          console.log('Select tool not active, ignoring click. Current tool:', this.activeTool);
        }
      });

      // Check if this layer should be immediately editable
      if (this.currentTool === 'edit-shape' &&
        this.selectedFeature &&
        this.selectedFeature.id === zonepart.id &&
        !this.isEditingVertex) { // Don't auto-enable during vertex editing
        this.enableEditing(layer);
      }
    },
    clearSelection() {
      // End vertex edit session if active
      if (this.vertexEditSession) {
        this.endVertexEditSession();
      }

      // End move session if active
      if (this.currentMoveableLayer) {
        this.disableMoveMode();
      }

      // Disable all editing
      this.disableAllEditing();

      // Clear sub-selection
      this.selectedPartIndex = null;
      this.removePartHighlight();

      // Reset tool to select
      this.currentTool = 'select';
      this.setActiveTool('select');

      // Clear selection in Vuex
      this.$store.dispatch('serviceAreas/clearSelection');
    },

    startEditSession(layer) {
      // Initialize vertex edit session when entering edit mode
      if (!layer || !layer.zonepart) {
        console.warn('Cannot start edit session - invalid layer:', layer);
        return;
      }

      console.log('Starting edit session for zonepart:', layer.zonepart.id);

      // CRITICAL: Cache the current editable zoneparts BEFORE setting isEditingVertex
      // This prevents the layer from disappearing when the computed property recalculates
      const currentEditableZoneparts = this.editableZoneparts;

      // Create new vertex edit session
      this.vertexEditSession = {
        active: true,
        parentLayer: layer,
        zonepartId: layer.zonepart.id,
        selectedFeature: this.selectedFeature,
        currentTool: 'edit',
        historySaved: false,
        cachedEditableZoneparts: currentEditableZoneparts  // Store the cache!
      };

      this.isEditingVertex = true;
      console.log('Edit session started successfully with cached zoneparts:', currentEditableZoneparts.length);
    },

    enableEditing(layer) {
      try {
        if (!layer || !layer.zonepart || !layer.zonepart.id) {
          console.warn('Cannot enable editing - invalid layer or missing zonepart data:', layer);
          return;
        }

        const zonepartId = layer.zonepart.id;
        console.log('Enabling editing for zonepart:', zonepartId);

        // Debug: Check layer visibility before changes
        console.log('Layer on map before editing?', this.map.hasLayer(layer));
        console.log('Layer options before:', layer.options);

        const editingStyle = this.getEditingStyle(layer.zonepart);
        console.log('Editing style to apply:', editingStyle);

        // DIFFERENT APPROACH: Create a new editable polygon from the GeoJSON geometry
        // Leaflet.Editable might have issues with GeoJSON child layers
        const geometry = layer.zonepart.geometry;
        if (!geometry || (geometry.type !== 'Polygon' && geometry.type !== 'MultiPolygon')) {
          console.warn('Invalid geometry for editing:', geometry?.type);
          return;
        }

        // Hide the original GeoJSON layer
        layer.setStyle({ opacity: 0, fillOpacity: 0 });

        // Handle both Polygon and MultiPolygon geometries
        let editablePolygons = [];

        if (geometry.type === 'Polygon') {
          // Single polygon (may have holes/interior rings)
          const rings = geometry.coordinates.map(ring =>
            ring.map(coord => [coord[1], coord[0]]) // Swap lng/lat to lat/lng for each ring
          );

          // First ring is the outer boundary, additional rings are holes
          const hasHoles = rings.length > 1;
          console.log('Creating editable polygon with', rings[0].length, 'points',
            hasHoles ? `and ${rings.length - 1} hole(s)` : '(no holes)');

          // Create a new editable polygon with all rings (outer + holes)
          const editablePolygon = L.polygon(rings, editingStyle).addTo(this.map);

          // Enable editing on this polygon
          editablePolygon.enableEdit();
          editablePolygons.push(editablePolygon);

        } else if (geometry.type === 'MultiPolygon') {
          // Multiple polygons
          console.log('Creating editable MultiPolygon with', geometry.coordinates.length, 'polygons');

          geometry.coordinates.forEach((polygonCoords, index) => {
            // Each polygon in the MultiPolygon
            const coordinates = polygonCoords[0]; // First ring of this polygon
            const latLngs = coordinates.map(coord => [coord[1], coord[0]]); // Swap lng/lat to lat/lng

            console.log(`Creating editable polygon ${index + 1} with ${latLngs.length} points`);

            // Create a new editable polygon for each part
            const editablePolygon = L.polygon(latLngs, editingStyle).addTo(this.map);

            // Enable editing on this polygon
            editablePolygon.enableEdit();
            editablePolygons.push(editablePolygon);
          });
        }

        console.log('Created and enabled', editablePolygons.length, 'editable polygon(s)');

        // Store reference to both layers
        this.currentEditableLayer = {
          geoJsonLayer: layer,
          editablePolygons: editablePolygons, // Now an array for MultiPolygon support
          editablePolygon: editablePolygons[0], // Keep for backward compatibility
          zonepartId: zonepartId,
          isMultiPolygon: geometry.type === 'MultiPolygon'
        };

        // Add event handlers for vertex editing to all polygons
        editablePolygons.forEach(polygon => {
          polygon.on('editable:vertex:dragstart', this.startVertexEditSession);
          polygon.on('editable:vertex:dragend', this.handleVertexEdit);
          polygon.on('editable:vertex:deleted', this.handleVertexEdit);
          polygon.on('editable:vertex:new', this.handleVertexEdit);
        });

        console.log('Editing enabled successfully with new approach');

        return;

        // OLD APPROACH BELOW (keeping for reference)
        /*
        // Get all polygon layers from the GeoJSON layer
        const layers = [];
        layer.eachLayer((childLayer) => {
          if (childLayer instanceof L.Polygon || childLayer instanceof L.Polyline) {
            layers.push(childLayer);
            console.log('Child layer type:', childLayer.constructor.name, 'Options:', childLayer.options);
          }
        });

        if (layers.length === 0) {
          console.warn('No polygon layers found in GeoJSON layer');
          return;
        }

        console.log('Found', layers.length, 'polygon layers to enable editing on');
        */

        // Enable Leaflet.Editable editing on each polygon
        layers.forEach((polygonLayer, index) => {
          console.log(`Enabling editing on polygon ${index + 1}/${layers.length}`);

          // Apply the editing style to each child polygon
          // This is crucial - we need to style the actual polygon, not just the parent
          polygonLayer.setStyle(editingStyle);
          console.log('Applied style to polygon:', editingStyle);

          // Check current visibility
          const currentOpacity = polygonLayer.options.opacity || 1;
          const currentFillOpacity = polygonLayer.options.fillOpacity || 0.2;
          console.log('Current opacity before enableEdit:', currentOpacity, 'Fill:', currentFillOpacity);

          // Enable editing using Leaflet.Editable
          // Always enable to ensure vertices are immediately editable
          if (!polygonLayer.editor) {
            console.log('Enabling edit on polygon layer');
            try {
              // Make sure the polygon is added to the map
              if (!this.map.hasLayer(polygonLayer)) {
                console.warn('Polygon not on map, adding it');
                polygonLayer.addTo(this.map);
              }

              // Enable editing
              polygonLayer.enableEdit();
              console.log('Editor enabled for polygon', index + 1);
              console.log('Editor object:', polygonLayer.editor);

              // Check if vertices were created
              if (polygonLayer.editor) {
                console.log('Editor tools:', polygonLayer.editor.tools);
                console.log('Editor enabled state:', polygonLayer.editor.enabled);

                // Force refresh of vertices
                if (polygonLayer.editor.reset) {
                  console.log('Resetting editor to refresh vertices');
                  polygonLayer.editor.reset();
                }
              }
            } catch (err) {
              console.error('Error enabling edit:', err);
            }
          } else {
            console.log('Editor already exists for polygon', index + 1);
            // Ensure editing is active even if editor exists
            if (polygonLayer.editor && !polygonLayer.editor.enabled) {
              polygonLayer.enableEdit();
            }
          }

          // CRITICAL: Re-apply style AFTER enabling edit
          // Leaflet.Editable might reset styles when enabling
          polygonLayer.setStyle(editingStyle);

          // Force visibility
          if (polygonLayer.setOpacity) {
            polygonLayer.setOpacity(1);
          }

          // Check visibility after enabling edit
          console.log('Opacity after enableEdit:', polygonLayer.options.opacity, 'Fill:', polygonLayer.options.fillOpacity);

          // Store reference to the parent GeoJSON layer
          polygonLayer._parentGeoJsonLayer = layer;

          // Add vertex event handlers if not already added
          if (!polygonLayer._vertexHandlersAdded) {
            // Listen for vertex drag events
            polygonLayer.on('editable:vertex:dragstart', this.startVertexEditSession);
            polygonLayer.on('editable:vertex:dragend', this.handleVertexEdit);
            polygonLayer.on('editable:vertex:deleted', this.handleVertexEdit);
            polygonLayer.on('editable:vertex:new', this.handleVertexEdit);
            polygonLayer._vertexHandlersAdded = true;
          }
        });

        // Try setting style on parent layer as well (may not work with GeoJSON layer)
        try {
          layer.setStyle(editingStyle);
        } catch (e) {
          console.log('Could not set style on parent layer:', e.message);
        }

        // Bring the layer to front to ensure it's visible
        if (layer.bringToFront) {
          layer.bringToFront();
          console.log('Brought layer to front');
        }

        // Store the current editable layer
        this.currentEditableLayer = layer;

        // Double-check the layer is still on the map
        if (this.map && !this.map.hasLayer(layer)) {
          console.warn('Layer was removed from map during editing enable, re-adding it');
          layer.addTo(this.map);
        }

        console.log('Editing enabled successfully for zonepart:', zonepartId);
        console.log('Final layer visibility:', this.map.hasLayer(layer));

      } catch (error) {
        console.error('Error enabling editing:', error);
      }
    },
    disableEditing(layer) {
      try {
        // Handle new approach with separate editable polygon(s)
        if (this.currentEditableLayer && (this.currentEditableLayer.editablePolygons || this.currentEditableLayer.editablePolygon)) {
          console.log('Disabling editing on editable polygon(s)');

          const { geoJsonLayer, editablePolygons, editablePolygon, isMultiPolygon } = this.currentEditableLayer;

          // Get the list of polygons to disable (handle both single and multi)
          const polygonsToDisable = editablePolygons || [editablePolygon];

          // Disable editing on all editable polygons
          polygonsToDisable.forEach(polygon => {
            if (polygon) {
              // Disable editing
              if (polygon.editor) {
                polygon.disableEdit();
              }

              // Remove event handlers
              polygon.off('editable:vertex:dragstart');
              polygon.off('editable:vertex:dragend');
              polygon.off('editable:vertex:deleted');
              polygon.off('editable:vertex:new');

              // Remove the editable polygon from the map
              if (this.map.hasLayer(polygon)) {
                this.map.removeLayer(polygon);
              }
            }
          });

          // Restore visibility to the original GeoJSON layer
          if (geoJsonLayer) {
            const normalStyle = this.getEditableStyle(geoJsonLayer.zonepart);
            geoJsonLayer.setStyle(normalStyle);
          }

          // Clear the reference
          this.currentEditableLayer = null;

          console.log('Editing disabled successfully');
          return;
        }

        // Fallback to old approach if needed
        if (!layer || !layer.zonepart || !layer.zonepart.id) {
          console.warn('Cannot disable editing - invalid layer or missing zonepart data:', layer);
          return;
        }

        const zonepartId = layer.zonepart.id;
        console.log('Disabling editing for zonepart:', zonepartId);

        // First restore the normal style to the parent GeoJSON layer
        const normalStyle = this.getEditableStyle(layer.zonepart);
        layer.setStyle(normalStyle);
        console.log('Restored normal style to parent layer:', normalStyle);

        // Get all child layers and disable editing
        const childLayers = [];
        layer.eachLayer((childLayer) => {
          if (childLayer instanceof L.Polygon || childLayer instanceof L.Polyline) {
            childLayers.push(childLayer);
          }
        });

        childLayers.forEach(childLayer => {
          try {
            // Disable Leaflet.Editable editing
            if (childLayer.editor) {
              childLayer.disableEdit();
            }

            // Remove edit event handlers
            childLayer.off('edit');
            childLayer.off('editable:vertex:dragstart');
            childLayer.off('editable:vertex:dragend');
            childLayer.off('editable:vertex:deleted');
            childLayer.off('editable:vertex:new');
            childLayer.off('editable:vertex:click');
            childLayer._vertexHandlersAdded = false;

            // Restore normal style to child layers as well
            if (childLayer.setStyle) {
              childLayer.setStyle(normalStyle);
            }

            // Ensure proper opacity
            if (childLayer.setOpacity) {
              childLayer.setOpacity(1);
            }
          } catch (error) {
            console.warn('Error disabling edit on child layer:', error);
          }
        });

        console.log('Editing disabled for zonepart:', zonepartId);

      } catch (error) {
        console.error('Error disabling editing:', error);
      }
    },
    disableAllEditing() {
      // Disable editing on all layers
      this.editableLayers.forEach(layer => this.disableEditing(layer));

      // Disable move mode if active
      if (this.currentMoveableLayer) {
        this.disableMoveMode();
      }

      // Cancel any current drawing
      if (this.currentDrawingLayer) {
        if (this.currentDrawingLayer.editor) {
          this.currentDrawingLayer.editor.cancelDrawing();
        }
        this.currentDrawingLayer = null;
      }
    },
    handleToolChange(tool) {
      console.log('Tool changed to:', tool, 'Selected feature:', this.selectedFeature?.id);

      // End vertex edit session when switching away from edit-shape tool
      if (this.currentTool === 'edit-shape' && tool !== 'edit-shape' && this.vertexEditSession) {
        this.endVertexEditSession();
      }

      // End move session when switching away from move tool
      if (this.currentTool === 'move' && tool !== 'move' && this.currentMoveableLayer) {
        this.disableMoveMode();
      }

      // Disable all current editing
      this.disableAllEditing();

      this.currentTool = tool;

      // Update Vuex store with new active tool
      this.setActiveTool(tool);

      // Handle different tools
      this.handleToolAction(tool);
    },

    handleToolAction(tool) {
      // Separate method to handle tool actions, can be called after edit mode is set
      switch (tool) {
        case 'select':
          // Just selection mode, nothing special to do
          break;

        case 'new-sector':
          this.promptForNewSector();
          break;

        case 'draw-rectangle':
          this.startDrawingRectangle();
          break;

        case 'edit-shape':
          if (this.selectedFeature && this.selectedFeature.type === 'zonepart') {
            const layer = this.editableLayers.get(this.selectedFeature.id);
            if (layer) {
              // Start vertex edit session immediately when entering edit-shape mode
              this.startEditSession(layer);
              this.enableEditing(layer);
            } else {
              console.warn('Layer not found for edit-shape mode, zonepart:', this.selectedFeature.id);
            }
          }
          break;

        case 'move':
          if (this.selectedFeature && this.selectedFeature.type === 'zonepart') {
            const layer = this.editableLayers.get(this.selectedFeature.id);
            if (layer) {
              // Choose implementation based on feature flag
              if (this.useNativePathDrag) {
                this.enableNativeMoveMode(layer);
              } else {
                this.enableMoveMode(layer);
              }
            } else {
              console.warn('Layer not found for move mode, zonepart:', this.selectedFeature.id);
            }
          }
          break;

        case 'delete':
          if (this.selectedFeature && this.selectedFeature.type === 'zonepart') {
            this.deleteSelectedFeature();
          }
          break;
      }
    },
    promptForNewSector() {
      // Show the new sector dialog
      this.showNewSectorDialog = true;
    },
    handleNewSectorConfirm(data) {
      // Store the new sector data
      this.newSectorData = data;
      this.showNewSectorDialog = false;

      // Start drawing the polygon
      this.startDrawingPolygon();
    },
    handleNewSectorCancel() {
      this.showNewSectorDialog = false;
      this.newSectorData = null;

      // Switch back to select tool
      this.handleToolChange('select');
    },
    startDrawingPolygon() {
      if (!this.map) return;

      console.log('Starting polygon drawing with Leaflet.Editable');

      // Start drawing a polygon
      this.currentDrawingLayer = this.map.editTools.startPolygon();

      // Apply drawing style
      if (this.currentDrawingLayer) {
        this.currentDrawingLayer.setStyle({
          fillColor: '#3388ff',
          fillOpacity: 0.2,
          color: '#2c5aa8',
          weight: 3
        });
      }
    },
    startDrawingRectangle() {
      if (!this.map) return;

      console.log('Starting rectangle drawing with Leaflet.Editable');

      // Start drawing a rectangle
      this.currentDrawingLayer = this.map.editTools.startRectangle();

      // Apply drawing style
      if (this.currentDrawingLayer) {
        this.currentDrawingLayer.setStyle({
          fillColor: '#3388ff',
          fillOpacity: 0.2,
          color: '#2c5aa8',
          weight: 3
        });
      }
    },
    handleDrawingComplete(layer) {
      if (!layer) return;

      console.log('Drawing complete:', layer);

      // Convert the drawn layer to GeoJSON
      const geojson = layer.toGeoJSON();

      if (geojson && geojson.geometry) {
        // Save to history for undo/redo
        this.saveToHistory();

        // Create new zonepart
        this.createNewZonepart(geojson.geometry);

        // Remove the temporary drawing layer
        if (this.map.hasLayer(layer)) {
          this.map.removeLayer(layer);
        }

        // Reset drawing state
        this.currentDrawingLayer = null;

        // Switch back to select mode
        this.$nextTick(() => {
          this.handleToolChange('select');
        });
      }
    },
    startVertexEditSession(e) {
      // If we already have an active session, don't create a new one
      if (this.vertexEditSession && this.vertexEditSession.active) {
        console.log('Vertex edit session already active, continuing with existing session');
        return;
      }

      console.log('Starting vertex edit session from drag event');

      // Find the parent layer for this vertex
      let parentLayer = null;
      let editedLayer = e.layer;

      if (editedLayer._parentGeoJsonLayer) {
        parentLayer = editedLayer._parentGeoJsonLayer;
      } else {
        this.editableLayers.forEach((layer, zonepartId) => {
          layer.eachLayer((childLayer) => {
            if (childLayer === editedLayer) {
              parentLayer = layer;
            }
          });
        });
      }

      if (parentLayer && parentLayer.zonepart) {
        // Only create a new session if we don't have one
        if (!this.vertexEditSession) {
          // CRITICAL: Cache BEFORE setting isEditingVertex
          const cachedZoneparts = [...this.editableZoneparts];

          this.isEditingVertex = true;
          this.vertexEditSession = {
            active: true,
            parentLayer: parentLayer,
            zonepartId: parentLayer.zonepart.id,
            originalGeometry: this.reconstructGeometryFromLayers(parentLayer),
            cachedEditableZoneparts: cachedZoneparts, // Use the cached value
            selectedFeature: { ...this.selectedFeature },
            currentTool: this.currentTool,
            historySaved: false
          };

          console.log('Vertex edit session started from drag with cache:', cachedZoneparts.length);
        }
      }
    },

    handleVertexEdit(e) {
      console.log('Vertex edit event:', e);

      // Ensure we have an active session
      if (!this.vertexEditSession) {
        console.warn('No active vertex edit session, starting one now');
        this.startVertexEditSession(e);
      }

      if (!this.vertexEditSession) {
        console.error('Failed to establish vertex edit session');
        this.isEditingVertex = false;
        return;
      }

      // Handle new approach with separate editable polygon
      if (this.currentEditableLayer && this.currentEditableLayer.editablePolygon) {
        const { editablePolygon, zonepartId } = this.currentEditableLayer;

        // Get the updated geometry from the editable polygon
        const updatedGeometry = editablePolygon.toGeoJSON().geometry;
        console.log('Updated geometry from editable polygon:', updatedGeometry);

        // Queue the geometry update
        this.pendingGeometryUpdate = {
          zonepartId: zonepartId,
          geometry: updatedGeometry
        };

        // Apply the update
        this.applyPendingGeometryUpdate();

        return;
      }

      // OLD APPROACH:
      const parentLayer = this.vertexEditSession.parentLayer;

      if (parentLayer && parentLayer.zonepart) {
        // Reconstruct the geometry from all child layers
        const updatedGeometry = this.reconstructGeometryFromLayers(parentLayer);

        if (updatedGeometry) {
          // Store pending geometry update but don't apply it yet
          this.pendingGeometryUpdate = {
            zonepartId: parentLayer.zonepart.id,
            geometry: updatedGeometry
          };

          // Apply the update after a short delay to batch multiple vertex operations
          if (this.pendingUpdateTimer) {
            clearTimeout(this.pendingUpdateTimer);
          }

          this.pendingUpdateTimer = setTimeout(() => {
            this.applyPendingGeometryUpdate();
          }, 50); // Small delay to batch rapid vertex updates

          console.log('Pending geometry update for zonepart:', parentLayer.zonepart.id);
        }
      } else {
        console.warn('Could not find parent layer for vertex edit');
        this.endVertexEditSession();
      }
    },

    applyPendingGeometryUpdate() {
      if (!this.pendingGeometryUpdate || !this.vertexEditSession) {
        return;
      }

      console.log('Applying pending geometry update');

      const { zonepartId, geometry } = this.pendingGeometryUpdate;
      const session = this.vertexEditSession;

      // Save to history only once per drag operation
      if (!this.vertexEditSession.historySaved) {
        this.saveToHistory();
        this.vertexEditSession.historySaved = true;
      }

      // Update the geometry directly in the layer without triggering reactivity
      const parentLayer = session.parentLayer;
      if (parentLayer) {
        // Update the internal zonepart data
        parentLayer.zonepart = {
          ...parentLayer.zonepart,
          geometry: geometry
        };

        // Update in store but prevent layer recreation
        this.updateZonepartGeometryQuietly(zonepartId, geometry);
      }

      // Clear pending update
      this.pendingGeometryUpdate = null;

      // REMOVED: setTimeout that was ending the session after geometry update
      // Session should persist throughout the entire edit operation
      // Reset the historySaved flag for next drag operation
      if (this.vertexEditSession) {
        this.vertexEditSession.historySaved = false;
      }
    },

    updateZonepartGeometryQuietly(zonepartId, geometry) {
      // Find the zonepart in the store
      const zonepartIndex = this.zoneparts.findIndex(zp => zp.id === zonepartId);
      if (zonepartIndex !== -1) {
        // Update the geometry directly without triggering watchers
        const zonepart = this.zoneparts[zonepartIndex];

        // Use Vue.set to update just the geometry property
        this.$set(this.zoneparts[zonepartIndex], 'geometry', geometry);

        // Mark as dirty
        this.setDirty(true);

        // Remove cached geometry
        this.removeCachedGeometry(zonepartId);
      }
    },

    endVertexEditSession() {
      if (!this.vertexEditSession) {
        this.isEditingVertex = false;
        return;
      }

      console.log('Ending vertex edit session');

      const session = this.vertexEditSession;

      // Ensure selection is maintained
      if (session.selectedFeature) {
        this.selectFeature(session.selectedFeature);
      }

      // Re-enable editing if needed
      if (session.currentTool === 'edit-shape' && session.parentLayer) {
        // The layer should still be in edit mode, just ensure vertices are visible
        const layer = this.editableLayers.get(session.zonepartId);
        if (layer && layer !== session.parentLayer) {
          // Layer was recreated, need to re-enable editing
          this.$nextTick(() => {
            this.enableEditing(layer);
          });
        }
      }

      // Clear session
      this.vertexEditSession = null;
      this.isEditingVertex = false;

      console.log('Vertex edit session ended');
    },
    reconstructGeometryFromLayers(geoJsonLayer) {
      // Reconstruct GeoJSON geometry from child layers
      try {
        const geometries = [];

        geoJsonLayer.eachLayer((layer) => {
          if (layer instanceof L.Polygon || layer instanceof L.Polyline) {
            const layerGeoJSON = layer.toGeoJSON();
            if (layerGeoJSON && layerGeoJSON.geometry) {
              geometries.push(layerGeoJSON.geometry);
            }
          }
        });

        if (geometries.length === 0) {
          console.warn('No geometries found in layer');
          return null;
        }

        // If only one geometry, return it directly
        if (geometries.length === 1) {
          return geometries[0];
        }

        // Multiple geometries - create a MultiPolygon
        return {
          type: 'MultiPolygon',
          coordinates: geometries.map(g => {
            if (g.type === 'Polygon') {
              return g.coordinates;
            } else if (g.type === 'MultiPolygon') {
              return g.coordinates[0]; // Take first polygon from MultiPolygon
            }
            return null;
          }).filter(Boolean)
        };

      } catch (error) {
        console.error('Error reconstructing geometry:', error);
        return null;
      }
    },
    enableNativeMoveMode(layer) {
      if (!layer || !layer.zonepart) {
        console.warn('Cannot enable native move mode - invalid layer');
        return;
      }

      const zonepartId = layer.zonepart.id;
      console.log('Enabling native move mode for zonepart:', zonepartId);

      // 1. Get the geometry
      const geometry = layer.zonepart.geometry;
      if (!geometry || (geometry.type !== 'Polygon' && geometry.type !== 'MultiPolygon')) {
        console.warn('Invalid geometry for native moving');
        return;
      }

      // Cache current editable zoneparts BEFORE setting isMoving
      const currentEditableZoneparts = this.editableZoneparts;

      // Create simplified move session for native dragging
      this.moveSession = {
        active: true,
        geoJsonLayer: layer,
        zonepartId: zonepartId,
        cachedEditableZoneparts: currentEditableZoneparts,
        originalGeometry: geometry,
        isNativeMode: true  // Flag to identify native mode
      };

      // Set isMoving flag to prevent layer recreation
      this.isMoving = true;

      // Convert GeoJSON coordinates to Leaflet LatLngs
      let latLngs;
      if (geometry.type === 'Polygon') {
        // Polygon: coordinates = [ring, ring, ...] where each ring = [[lng,lat], ...]
        latLngs = geometry.coordinates.map(ring =>
          ring.map(coord => [coord[1], coord[0]])
        );
      } else {
        // MultiPolygon: coordinates = [polygon, polygon, ...] where each polygon = [ring, ring, ...]
        latLngs = geometry.coordinates.map(polygon =>
          polygon.map(ring =>
            ring.map(coord => [coord[1], coord[0]])
          )
        );
      }

      console.log('Creating native draggable polygon from coordinates');

      // 2. Hide original layer
      layer.setStyle({ opacity: 0, fillOpacity: 0 });

      // 3. Create draggable polygon with move style (orange dashed)
      const moveStyle = {
        fillColor: '#ff9900',
        fillOpacity: 0.4,
        color: '#ff6600',
        weight: 3,
        dashArray: '5,5',
        interactive: true
      };

      // Create polygon with draggable option - REQUIRED for PathDrag to work
      const draggablePolygon = L.polygon(latLngs, {
        ...moveStyle,
        draggable: true  // CRITICAL: Must be set during creation for PathDrag
      }).addTo(this.map);

      // Ensure the polygon is properly rendered and in front
      draggablePolygon.bringToFront();

      // Set cursor style to indicate draggable
      if (draggablePolygon._path) {
        draggablePolygon._path.style.cursor = 'move';
      }

      // The dragging handler should now exist automatically from draggable: true
      if (draggablePolygon.dragging && typeof draggablePolygon.dragging.enable === 'function') {
        console.log('Enabling auto-created PathDrag handler');
        draggablePolygon.dragging.enable();

        // Remove click handlers that interfere with dragging
        draggablePolygon.off('click');
        console.log('PathDrag handler ready');

        // PathDrag fires these events automatically
        draggablePolygon.on('dragstart', (e) => {
          console.log('Native drag started');
          this.saveToHistory();
          // PathDrag automatically handles map dragging disable/enable
        });

        draggablePolygon.on('dragend', (e) => {
          console.log('Native drag ended');

          // Get the updated geometry from the dragged polygon
          const updatedGeometry = draggablePolygon.toGeoJSON().geometry;

          // Update the zonepart geometry locally
          this.updateZonepartLocally(zonepartId, { geometry: updatedGeometry });

          // Update the layer's internal geometry reference
          if (layer.zonepart) {
            layer.zonepart.geometry = updatedGeometry;
          }

          // Sync the original GeoJSON layer's coordinates so it shows
          // at the correct position when move mode is disabled
          layer.clearLayers();
          layer.addData(updatedGeometry);
          layer.setStyle({ opacity: 0, fillOpacity: 0 });

          console.log('Native move completed, geometry updated');
        });

        console.log('Native move mode enabled with PathDrag');
      } else {
        // PathDrag not available, fallback to legacy implementation
        console.log('PathDrag handler not available, falling back to legacy move implementation');

        // Clean up the polygon we just created
        if (this.map.hasLayer(draggablePolygon)) {
          this.map.removeLayer(draggablePolygon);
        }

        // Reset state
        this.moveSession = null;
        this.isMoving = false;

        // Restore original layer visibility
        const normalStyle = this.getEditableStyle(layer.zonepart);
        layer.setStyle(normalStyle);

        // Use the working legacy implementation
        this.enableMoveMode(layer);
        return;
      }

      // 6. Store references for cleanup
      this.currentMoveableLayer = {
        geoJsonLayer: layer,
        moveablePolygon: draggablePolygon,  // The draggable polygon (native transform)
        dragMarker: null,  // No drag marker needed for native mode
        zonepartId: zonepartId,
        isNativeMode: true
      };
    },
    enableMoveMode(layer) {
      if (!layer || !layer.zonepart) {
        console.warn('Cannot enable move mode - invalid layer');
        return;
      }

      const zonepartId = layer.zonepart.id;
      console.log('Enabling move mode for zonepart:', zonepartId);

      // Get the geometry
      const geometry = layer.zonepart.geometry;
      if (!geometry || (geometry.type !== 'Polygon' && geometry.type !== 'MultiPolygon')) {
        console.warn('Invalid geometry for moving');
        return;
      }

      // Cache current editable zoneparts BEFORE setting isMoving
      const currentEditableZoneparts = this.editableZoneparts;

      // Create move session
      this.moveSession = {
        active: true,
        geoJsonLayer: layer,
        zonepartId: zonepartId,
        cachedEditableZoneparts: currentEditableZoneparts,
        originalGeometry: geometry,
        originalLatLngs: null  // Will store original position for drag tracking
      };

      // Set isMoving flag to prevent layer recreation
      this.isMoving = true;

      // Hide the original GeoJSON layer
      layer.setStyle({ opacity: 0, fillOpacity: 0 });

      // Convert GeoJSON coordinates to Leaflet LatLngs
      let latLngs;
      if (geometry.type === 'Polygon') {
        // Polygon: coordinates = [ring, ring, ...] where each ring = [[lng,lat], ...]
        latLngs = geometry.coordinates.map(ring =>
          ring.map(coord => [coord[1], coord[0]])
        );
      } else {
        // MultiPolygon: coordinates = [polygon, polygon, ...] where each polygon = [ring, ring, ...]
        latLngs = geometry.coordinates.map(polygon =>
          polygon.map(ring =>
            ring.map(coord => [coord[1], coord[0]])
          )
        );
      }

      console.log('Creating draggable polygon from coordinates');

      // Create moveable polygon with move style
      const moveStyle = {
        fillColor: '#ff9900',
        fillOpacity: 0.4,
        color: '#ff6600',
        weight: 3,
        dashArray: '5,5',
        interactive: true
      };

      const moveablePolygon = L.polygon(latLngs, moveStyle).addTo(this.map);
      moveablePolygon.bringToFront();

      // Create an invisible draggable marker at the polygon's center
      const center = moveablePolygon.getBounds().getCenter();

      // Create invisible marker with a transparent icon
      const invisibleIcon = L.divIcon({
        className: 'drag-handle-marker',
        iconSize: [1, 1],
        iconAnchor: [0, 0]
      });

      const dragMarker = L.marker(center, {
        draggable: true,
        icon: invisibleIcon,
        zIndexOffset: 1000 // Ensure it's above other elements
      }).addTo(this.map);

      // Store the original polygon coordinates
      let originalLatLngs = moveablePolygon.getLatLngs();
      let originalCenter = center;

      // Handle marker drag start
      dragMarker.on('dragstart', (e) => {
        console.log('Marker drag started for Move tool');
        originalLatLngs = moveablePolygon.getLatLngs();
        originalCenter = e.target.getLatLng();
        this.saveToHistory();

        // Disable map dragging
        if (this.map.dragging) {
          this.map.dragging.disable();
        }
      });

      // Handle marker dragging
      dragMarker.on('drag', (e) => {
        const newCenter = e.target.getLatLng();

        // Calculate the offset
        const latOffset = newCenter.lat - originalCenter.lat;
        const lngOffset = newCenter.lng - originalCenter.lng;

        // Apply offset to all polygon vertices
        const newLatLngs = this.offsetLatLngs(originalLatLngs, { lat: latOffset, lng: lngOffset });
        moveablePolygon.setLatLngs(newLatLngs);
      });

      // Handle marker drag end
      dragMarker.on('dragend', (e) => {
        console.log('Marker drag ended for Move tool');

        // Re-enable map dragging
        if (this.map.dragging) {
          this.map.dragging.enable();
        }

        // Save the final polygon position
        const updatedGeometry = moveablePolygon.toGeoJSON().geometry;
        this.updateZonepartLocally(zonepartId, { geometry: updatedGeometry });

        if (layer.zonepart) {
          layer.zonepart.geometry = updatedGeometry;
        }

        // Sync the original GeoJSON layer's coordinates so it shows
        // at the correct position when move mode is disabled
        layer.clearLayers();
        layer.addData(updatedGeometry);
        layer.setStyle({ opacity: 0, fillOpacity: 0 });

        // Mark as dirty since we made a change
        this.setDirty(true);

        // Update the marker reference position
        originalCenter = e.target.getLatLng();
        originalLatLngs = moveablePolygon.getLatLngs();
      });

      // Make the polygon clickable to initiate drag
      moveablePolygon.on('mousedown', (e) => {
        // Trigger marker drag programmatically
        const markerElement = dragMarker._icon;
        if (markerElement) {
          // Create and dispatch a mousedown event on the marker
          const evt = new MouseEvent('mousedown', {
            bubbles: true,
            cancelable: true,
            view: window,
            clientX: e.originalEvent.clientX,
            clientY: e.originalEvent.clientY
          });
          markerElement.dispatchEvent(evt);
        }
      });

      // Store references for cleanup
      this.currentMoveableLayer = {
        geoJsonLayer: layer,
        moveablePolygon: moveablePolygon,
        dragMarker: dragMarker,
        zonepartId: zonepartId
      };

      console.log('Move mode enabled with marker-based dragging');
    },

    async waitForEditableLayer(zonepartId) {
      // Wait for the editable layer to be created by Vue's reactive system
      const maxAttempts = 20; // 20 attempts
      const delay = 50; // 50ms between attempts = 1 second total

      for (let i = 0; i < maxAttempts; i++) {
        if (this.editableLayers.has(zonepartId)) {
          console.log(`Editable layer found for zonepart ${zonepartId} after ${i} attempts`);
          return this.editableLayers.get(zonepartId);
        }
        await new Promise(resolve => setTimeout(resolve, delay));
      }

      console.warn(`Editable layer not found for zonepart ${zonepartId} after ${maxAttempts} attempts`);
      return null;
    },
    disableMoveMode() {
      console.log('Disabling move mode');

      // Re-enable map dragging
      if (this.map && this.map.dragging) {
        this.map.dragging.enable();
        console.log('Map dragging re-enabled');
      }

      if (this.currentMoveableLayer) {
        const { dragMarker, moveablePolygon, geoJsonLayer, isNativeMode } = this.currentMoveableLayer;

        if (isNativeMode) {
          // Handle native path drag cleanup
          console.log('Disabling native move mode');

          if (moveablePolygon) {
            // Disable dragging if it was enabled
            if (moveablePolygon.dragging && moveablePolygon.dragging.disable) {
              moveablePolygon.dragging.disable();
            }

            // Remove event listeners
            moveablePolygon.off('dragstart dragend');

            // Remove the moveable polygon from the map
            if (this.map.hasLayer(moveablePolygon)) {
              this.map.removeLayer(moveablePolygon);
            }
          }
        } else {
          // Handle legacy implementation cleanup
          console.log('Disabling legacy move mode');

          // Remove the drag marker
          if (dragMarker && this.map.hasLayer(dragMarker)) {
            dragMarker.off(); // Remove all event listeners
            this.map.removeLayer(dragMarker);
          }

          // Remove the moveable polygon from the map
          if (moveablePolygon && this.map.hasLayer(moveablePolygon)) {
            moveablePolygon.off(); // Remove mousedown listener
            this.map.removeLayer(moveablePolygon);
          }
        }

        // Restore visibility to the original GeoJSON layer (common to both implementations)
        if (geoJsonLayer) {
          const normalStyle = this.getEditableStyle(geoJsonLayer.zonepart);
          geoJsonLayer.setStyle(normalStyle);
        }

        // Clear references
        this.currentMoveableLayer = null;
        this.moveSession = null;
        this.isMoving = false;

        console.log('Move mode disabled successfully');
      }
    },
    offsetLatLngs(latlngs, offset) {
      // Recursively offset all coordinates
      if (Array.isArray(latlngs[0])) {
        return latlngs.map(ll => this.offsetLatLngs(ll, offset));
      } else {
        return latlngs.map(ll => L.latLng(ll.lat + offset.lat, ll.lng + offset.lng));
      }
    },
    deleteSelectedFeature() {
      if (!this.selectedFeature || this.selectedFeature.type !== 'zonepart') {
        return;
      }

      // Sub-selection branch: delete only one polygon of a MultiPolygon
      if (this.selectedPartIndex !== null) {
        return this.deleteSelectedPart();
      }

      if (confirm(`Are you sure you want to delete this sector?`)) {
        this.saveToHistory();

        // Call the store action to delete the zonepart
        this.deleteZonepart({
          schoolId: this.schoolId,
          zonepartId: this.selectedFeature.id
        }).then((result) => {
          // Clear selection first
          this.clearSelection();

          // Center the map based on deletion result
          if (result.zone_deleted) {
            // Zone was deleted, center on entire service area
            if (this.zoneparts && this.zoneparts.length > 0) {
              this.centerMapOnZoneparts();
            }
          } else {
            // Zone still exists, center on remaining zoneparts in the same zone
            const remainingZoneparts = this.zoneparts.filter(
              zp => zp.zone_id === result.zone_id
            );

            if (remainingZoneparts.length > 0 && this.map) {
              let bounds = L.latLngBounds();
              let hasValidBounds = false;

              remainingZoneparts.forEach(zp => {
                if (zp && zp.geometry && GeometryService.isValidGeometry(zp.geometry)) {
                  try {
                    const geometryBounds = GeometryService.calculateBounds(zp.geometry);
                    if (geometryBounds && geometryBounds.isValid()) {
                      bounds.extend(geometryBounds);
                      hasValidBounds = true;
                    }
                  } catch (error) {
                    console.warn('Error calculating bounds for zonepart:', zp.id, error);
                  }
                }
              });

              if (hasValidBounds) {
                this.map.fitBounds(bounds, {
                  padding: [50, 50],
                  maxZoom: 16
                });
              }
            }
          }

          // Switch back to select mode
          this.handleToolChange('select');

          // Show success message
          console.log(result.message);
        }).catch(error => {
          console.error('Failed to delete sector:', error);
          alert('Failed to delete sector. Please try again.');
        });
      }
    },
    /**
     * Sidebar emitted that a polygon part should be selected. Make sure the parent
     * sector is selected first, then set the sub-selection and update the highlight.
     */
    handleSelectPart({ zonepartId, partIndex }) {
      if (!this.selectedFeature || this.selectedFeature.id !== zonepartId) {
        const zp = this.zoneparts.find(z => z.id === zonepartId);
        if (!zp) return;
        this.selectZonepart(zp);
      }
      this.$nextTick(() => {
        this.selectedPartIndex = partIndex;
        this.updatePartHighlight();
      });
    },
    /**
     * Sidebar emitted that a polygon part should be deleted. Make sure the part
     * is selected (so deleteSelectedPart's confirm prompt and validation are right),
     * then run the existing deletion flow.
     */
    handleDeletePart({ zonepartId, partIndex }) {
      if (!this.selectedFeature || this.selectedFeature.id !== zonepartId) {
        const zp = this.zoneparts.find(z => z.id === zonepartId);
        if (!zp) return;
        this.selectZonepart(zp);
      }
      this.selectedPartIndex = partIndex;
      this.updatePartHighlight();
      this.$nextTick(() => this.deleteSelectedPart());
    },
    /**
     * Removes a single polygon from a MultiPolygon sector. Mutates the geometry
     * locally and marks the zonepart dirty (same flow as vertex edits / move).
     * Persists on Save via the existing PUT /zoneparts/{id} endpoint.
     */
    deleteSelectedPart() {
      if (this.selectedPartIndex === null) return;
      const zonepart = this.selectedZonepart;
      if (!zonepart || zonepart.geometry?.type !== 'MultiPolygon') {
        // Defensive: clear stale sub-selection
        this.selectedPartIndex = null;
        this.removePartHighlight();
        return;
      }

      const name = zonepart.name || `Sector ${zonepart.id}`;
      const confirmed = confirm(`Delete this part of ${name}? Other parts will remain.`);

      if (confirmed) {
        this.saveToHistory();

        const newGeometry = GeometryService.removePolygonFromMultiPolygon(
          zonepart.geometry,
          this.selectedPartIndex
        );

        // Same dirty-tracking helper used by vertex edits and move
        this.updateZonepartGeometryQuietly(zonepart.id, newGeometry);

        // Clear sub-selection; whole sector remains selected
        this.selectedPartIndex = null;
        this.removePartHighlight();
      }

      // Always return tool to select so the user is not stuck in 'delete' on cancel
      this.handleToolChange('select');
    },
    createNewZonepart(geometry) {
      // Use the stored new sector data if available
      if (this.newSectorData) {
        const newZonepart = {
          name: this.newSectorData.name,
          zone_id: this.newSectorData.zone_id,
          zone_name: this.newSectorData.zone_name,
          school_id: this.schoolId,
          geometry: geometry
        };

        // Add location data if present
        if (this.newSectorData.location_id !== undefined) {
          newZonepart.location_id = this.newSectorData.location_id;
        }
        if (this.newSectorData.location_name) {
          newZonepart.location_name = this.newSectorData.location_name;
        }
        // When creating a new location (location_id is 0), the API expects 'location_address'
        // Otherwise, use 'address' for existing locations
        if (this.newSectorData.address) {
          if (this.newSectorData.location_id === 0) {
            newZonepart.location_address = this.newSectorData.address;
          } else {
            newZonepart.address = this.newSectorData.address;
          }
        }

        // Clear the stored data
        this.newSectorData = null;

        // Call the store action to create the zonepart
        this.createZonepart(newZonepart).then((createdZonepart) => {
          if (createdZonepart) {
            // Select the newly created zonepart
            this.selectZonepart(createdZonepart);

            // Center map on the newly created zonepart
            if (createdZonepart.geometry && this.map) {
              try {
                const geoJson = L.geoJSON(createdZonepart.geometry);
                const bounds = geoJson.getBounds();
                if (bounds.isValid()) {
                  this.map.fitBounds(bounds, {
                    padding: [50, 50],
                    maxZoom: 16
                  });
                }
              } catch (error) {
                console.error('Failed to center on new zonepart:', error);
              }
            }
          }
        }).catch(error => {
          console.error('Error creating zonepart:', error);
          alert('Failed to create new sector. Please try again.');
        });
      } else {
        // Fallback if no data (shouldn't happen)
        alert('No sector data available. Please try again.');
      }
    },
    saveToHistory() {
      // Save current state to history for undo/redo
      const currentState = {
        zoneparts: JSON.parse(JSON.stringify(this.zoneparts)),
        selectedFeature: this.selectedFeature ? { ...this.selectedFeature } : null
      };

      // Remove any history after current index
      this.editHistory = this.editHistory.slice(0, this.historyIndex + 1);

      // Add new state
      this.editHistory.push(currentState);
      this.historyIndex++;

      // Limit history size
      if (this.editHistory.length > 50) {
        this.editHistory.shift();
        this.historyIndex--;
      }
    },
    handleUndo() {
      if (this.canUndo) {
        this.historyIndex--;
        this.restoreHistoryState(this.editHistory[this.historyIndex]);
      }
    },
    handleRedo() {
      if (this.canRedo) {
        this.historyIndex++;
        this.restoreHistoryState(this.editHistory[this.historyIndex]);
      }
    },
    restoreHistoryState(state) {
      // Restore zoneparts
      state.zoneparts.forEach(zp => {
        this.updateZonepartLocally(zp.id, zp);
      });

      // Clear any MultiPolygon part sub-selection — its index may not map to the restored geometry
      this.selectedPartIndex = null;
      this.removePartHighlight();

      // Restore selection
      if (state.selectedFeature) {
        this.selectFeature(state.selectedFeature);
      } else {
        this.clearSelection();
      }
    },
    async handleSave() {
      // If no changes, do nothing
      if (!this.isDirty) {
        return;
      }

      // Save only the selected zonepart (per FEATURER_PLAN.md)
      if (this.selectedFeature && this.selectedFeature.type === 'zonepart') {
        await this.performSaveSelectedZonepart();
      } else {
        // Fallback to save all if no selection (shouldn't happen)
        await this.performSave();
      }
    },
    handleCancel() {
      if (this.isDirty) {
        this.showUnsavedChangesDialog = true;
      } else {
        // No changes, just switch back to select tool
        this.setActiveTool('select');
        this.currentTool = 'select';
      }
    },
    async performSave() {
      this.savingChanges = true;

      try {
        await this.saveAllChanges();
        // After save, switch to select tool
        this.setActiveTool('select');
        this.currentTool = 'select';

        // Clear history after successful save
        this.editHistory = [];
        this.historyIndex = -1;
      } catch (error) {
        console.error('Error saving changes:', error);
        alert('Failed to save changes. Please try again.');
      } finally {
        this.savingChanges = false;
      }
    },
    async performSaveSelectedZonepart() {
      this.savingChanges = true;

      try {
        // Save only the selected zonepart
        await this.saveSelectedZonepart();
        // After save, switch to select tool
        this.setActiveTool('select');
        this.currentTool = 'select';

        // Clear history after successful save
        this.editHistory = [];
        this.historyIndex = -1;
      } catch (error) {
        console.error('Error saving selected zonepart:', error);
        alert('Failed to save changes. Please try again.');
      } finally {
        this.savingChanges = false;
      }
    },
    handleConfirmDiscard() {
      this.showUnsavedChangesDialog = false;

      // Clean up any active editing layers (including moveable polygons)
      this.disableAllEditing();

      // Reset to clean state from store
      this.resetToCleanState();

      // Switch back to select tool and clear selection
      this.setActiveTool('select');
      this.currentTool = 'select';
      this.clearSelection();

      // Clear history
      this.editHistory = [];
      this.historyIndex = -1;

      // Clear any old local state tracking
      this.originalZoneparts = null;

      // Refresh layers to reflect restored state
      this.$nextTick(() => {
        this.refreshLayers();
      });

      // Handle pending selection if any
      if (this.pendingSelection) {
        const pending = this.pendingSelection;
        this.pendingSelection = null;
        this.selectZonepart(pending);
      }
    },
    handleConfirmCancel() {
      this.showUnsavedChangesDialog = false;
    },
    toggleZoneList() {
      this.zoneListCollapsed = !this.zoneListCollapsed;
    },
    handleEditZonepartName(zonepart) {
      // Handle edit name request from ZonePartsList
      this.editingItem = zonepart;
      this.editingName = zonepart.name || `Sector ${zonepart.id}`;
      this.editingZonepartName = true;

      this.$nextTick(() => {
        if (this.$refs.editNameInput) {
          this.$refs.editNameInput.focus();
          this.$refs.editNameInput.select();
        }
      });
    },
    async saveEditName() {
      if (!this.editingName.trim()) {
        return;
      }

      try {
        await this.updateZonepart({
          schoolId: this.schoolId,
          zonepartId: this.editingItem.id,
          data: { name: this.editingName.trim() }
        });

        this.editingItem.name = this.editingName.trim();
        this.cancelEditName();
      } catch (error) {
        console.error('Failed to update zonepart name:', error);
        alert('Failed to update name. Please try again.');
      }
    },
    cancelEditName() {
      this.editingZonepartName = false;
      this.editingItem = null;
      this.editingName = '';
    },
    handleZoomToZonepart(zonepart) {
      // Zoom to the bounds of a zonepart
      if (!zonepart.geometry || !this.map) return;

      try {
        const geoJson = L.geoJSON(zonepart.geometry);
        const bounds = geoJson.getBounds();

        if (bounds.isValid()) {
          this.map.fitBounds(bounds, {
            padding: [50, 50],
            maxZoom: 16
          });
        }
      } catch (error) {
        console.error('Failed to zoom to zonepart:', error);
      }
    },
    centerMapOnZoneparts() {
      // Center map on all zoneparts
      const validZoneparts = Array.isArray(this.zoneparts) ? this.zoneparts : [];
      if (validZoneparts.length > 0 && this.map) {
        let bounds = L.latLngBounds();
        let hasValidBounds = false;

        validZoneparts.forEach(zp => {
          if (zp && zp.geometry && GeometryService.isValidGeometry(zp.geometry)) {
            try {
              const geometryBounds = GeometryService.calculateBounds(zp.geometry);
              if (geometryBounds && geometryBounds.isValid()) {
                bounds.extend(geometryBounds);
                hasValidBounds = true;
              }
            } catch (error) {
              console.warn('Error calculating bounds for zonepart:', zp.id, error);
            }
          }
        });

        if (hasValidBounds) {
          this.map.fitBounds(bounds, {
            padding: [50, 50],
            maxZoom: 15
          });
        }
      }
    },
    getZoneName(zoneId) {
      // Helper method to get zone name for display
      const zone = this.getZoneById(zoneId);
      return zone ? zone.name : 'Unknown Zone';
    },
    handleFeatureSelect(feature) {
      // Handle feature selection from the zone parts list
      // According to plan: activate Select tool + select + zoom to sector

      if (feature.type === 'zonepart') {
        const zonepart = this.zoneparts.find(zp => zp.id === feature.id);
        if (zonepart) {
          // Activate Select tool first
          this.setActiveTool('select');
          this.currentTool = 'select';

          // Then select the zonepart
          this.selectZonepart(zonepart);

          // Auto-zoom/center since this selection came from the list
          // Reuse the existing zoom logic for consistency
          this.handleZoomToZonepart(zonepart);
        }
      } else if (feature.type === 'zone') {
        // For zones, you might want to highlight all zoneparts in that zone
        this.selectFeature(feature);
      }
    },
    selectZonepart(zonepart) {
      console.log('selectZonepart called:', {
        zonepartId: zonepart?.id,
        zonepartName: zonepart?.name,
        activeTool: this.activeTool,
        isDirty: this.isDirty,
        currentSelection: this.selectedFeature?.id
      });

      // Check for unsaved changes before switching selection
      if (this.isDirty && this.selectedFeature && this.selectedFeature.id !== zonepart.id) {
        console.log('Has unsaved changes, showing dialog');
        this.pendingSelection = zonepart;
        this.showUnsavedChangesDialog = true;
        return;
      }

      // Helper method to select a zonepart
      if (zonepart) {
        console.log('Selecting feature:', zonepart.id);
        this.selectFeature({
          id: zonepart.id,
          type: 'zonepart',
          data: zonepart
        });
      }
    },
    updateLayerStyles() {
      // Force update of layer styles
      this.backgroundLayers.forEach((layer, zonepartId) => {
        const zonepart = this.zoneparts.find(zp => zp.id === zonepartId);
        if (zonepart) {
          layer.setStyle(this.getBackgroundStyle(zonepart));
        }
      });

      this.editableLayers.forEach((layer, zonepartId) => {
        const zonepart = this.zoneparts.find(zp => zp.id === zonepartId);
        if (zonepart) {
          layer.setStyle(this.getEditableStyle(zonepart));
        }
      });
    },
    refreshLayers() {
      // Refresh layer geometries after state changes (e.g., cancel/discard)
      // This ensures the map reflects the current state from the store
      console.log('Refreshing map layers after state change');

      // Update styles which will reflect any geometry changes
      this.updateLayerStyles();

      // Force re-render of affected layers if needed
      // The watchers should handle most updates, but this ensures consistency
    },
    handleFullscreenChange() {
      // Check if we're still in fullscreen
      const isCurrentlyFullscreen = !!(document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement);

      if (this.isFullscreen !== isCurrentlyFullscreen) {
        this.isFullscreen = isCurrentlyFullscreen;
        if (this.$refs.mapContainer) {
          if (isCurrentlyFullscreen) {
            this.$refs.mapContainer.classList.add('is-fullscreen');
          } else {
            // Ensure clean removal of fullscreen class
            this.$refs.mapContainer.classList.remove('is-fullscreen');
            // Reset any inline styles that might have been added
            this.$refs.mapContainer.style.position = '';
            this.$refs.mapContainer.style.top = '';
            this.$refs.mapContainer.style.left = '';
            this.$refs.mapContainer.style.right = '';
            this.$refs.mapContainer.style.bottom = '';
            this.$refs.mapContainer.style.width = '';
            this.$refs.mapContainer.style.height = '';
            this.$refs.mapContainer.style.zIndex = '';
          }
        }

        // Update map size with a delay for CSS transitions to complete
        if (this.map) {
          setTimeout(() => {
            this.map.invalidateSize();
            // Also fire resize event to ensure complete redraw
            this.map.fire('resize');
            // Force a complete redraw if needed
            if (!isCurrentlyFullscreen) {
              this.map.fire('resize');
            }
          }, 350);
        }
      }
    },
    cleanupResources() {
      console.log('Cleaning up ServiceAreaMap resources...');

      // Disable all editing
      this.disableAllEditing();

      // Remove all layers from maps
      this.backgroundLayers.forEach((layer, zonepartId) => {
        try {
          console.log('Removing background layer for zonepart:', zonepartId);
          layer.off();
          if (this.map && this.map.hasLayer(layer)) {
            this.map.removeLayer(layer);
          }
        } catch (error) {
          console.warn(`Error removing background layer ${zonepartId}:`, error);
        }
      });
      this.backgroundLayers.clear();

      this.editableLayers.forEach((layer, zonepartId) => {
        try {
          console.log('Removing editable layer for zonepart:', zonepartId);
          layer.off();
          if (this.map && this.map.hasLayer(layer)) {
            this.map.removeLayer(layer);
          }
        } catch (error) {
          console.warn(`Error removing editable layer ${zonepartId}:`, error);
        }
      });
      this.editableLayers.clear();

      // Remove map event listeners
      if (this.map) {
        this.map.off();
      }

      // Clear references
      this.map = null;
      this.currentEditableLayer = null;
      this.currentDrawingLayer = null;

      console.log('ServiceAreaMap cleanup completed');
    }
  },
  mounted() {
    // Initialize activeTool in Vuex store if not set
    if (!this.activeTool) {
      this.setActiveTool('select');
    }

    // Initial setup
    const initialState = {
      zoneparts: JSON.parse(JSON.stringify(this.zoneparts)),
      selectedFeature: this.selectedFeature ? { ...this.selectedFeature } : null
    };
    this.editHistory = [initialState];
    this.historyIndex = 0;


    // Add fullscreen change event listener
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', this.handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', this.handleFullscreenChange);

    // Setup fullscreen control after a delay to ensure map is ready
    setTimeout(() => {
      this.setupFullscreenControl();
    }, 500);
  },
  beforeDestroy() {
    // Clean up any pending timers
    if (this.pendingUpdateTimer) {
      clearTimeout(this.pendingUpdateTimer);
    }

    // End any active vertex edit session
    if (this.vertexEditSession) {
      this.endVertexEditSession();
    }

    // End any active move session
    if (this.currentMoveableLayer) {
      this.disableMoveMode();
    }

    // Remove fullscreen event listeners
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('mozfullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('MSFullscreenChange', this.handleFullscreenChange);

    this.cleanupResources();
  }
};
</script>

<style scoped>
/* Ensure moveable polygon receives all mouse events */
.moveable-polygon {
  pointer-events: auto !important;
  cursor: move !important;
}

.service-area-map-container {
  position: relative;
  background: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
}

/* Map info control */
.map-info {
  background: white;
  padding: 10px;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  font-size: 14px;
  min-width: 200px;
}

.edit-mode-info {
  margin-bottom: 5px;
  color: #666;
}

.selected-info {
  color: #333;
  border-top: 1px solid #eee;
  padding-top: 5px;
  margin-top: 5px;
}

/* Selected Zonepart Panel */
.selected-zonepart-control {
  margin-top: 80px;
  /* Position below the edit mode info */
}

.selected-zonepart-panel {
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  min-width: 250px;
  overflow: hidden;
  animation: slideIn 0.3s ease-out;
}

.selected-zonepart-panel .panel-header {
  background: #f8f9fa;
  padding: 10px 12px;
  border-bottom: 1px solid #dee2e6;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #495057;
  font-size: 14px;
}

.selected-zonepart-panel .panel-header i {
  color: #28a745;
}

.selected-zonepart-panel .panel-content {
  padding: 12px;
}

.selected-zonepart-panel .info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
}

.selected-zonepart-panel .info-row:not(:last-child) {
  border-bottom: 1px solid #f0f0f0;
}

.selected-zonepart-panel .info-row label {
  font-weight: 600;
  color: #6c757d;
  font-size: 13px;
  margin: 0;
}

.selected-zonepart-panel .info-value {
  color: #212529;
  font-size: 14px;
  font-weight: 500;
  text-align: right;
  flex: 1;
  margin-left: 10px;
}

/* Animation for panel appearance */
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(20px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Zone legend */
.map-legend {
  background: white;
  padding: 10px;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  font-size: 14px;
}

.legend-title {
  font-weight: bold;
  margin-bottom: 8px;
  color: #333;
}

.legend-item {
  display: flex;
  align-items: center;
  margin-bottom: 5px;
}

.zone-color {
  width: 20px;
  height: 20px;
  border-radius: 3px;
  margin-right: 8px;
  border: 1px solid rgba(0, 0, 0, 0.2);
}

/* Zone labels on map */
:deep(.zone-label) {
  background: none !important;
  border: none !important;
  font-weight: bold;
  text-shadow: 2px 2px 4px rgba(255, 255, 255, 0.8),
    -2px -2px 4px rgba(255, 255, 255, 0.8),
    2px -2px 4px rgba(255, 255, 255, 0.8),
    -2px 2px 4px rgba(255, 255, 255, 0.8);
  color: #333;
  font-size: 16px;
  white-space: nowrap;
}

/* Zonepart/sector labels on map */
:deep(.zonepart-label) {
  background: none !important;
  border: none !important;
  font-weight: normal;
  text-shadow: 1px 1px 2px rgba(255, 255, 255, 0.9),
    -1px -1px 2px rgba(255, 255, 255, 0.9),
    1px -1px 2px rgba(255, 255, 255, 0.9),
    -1px 1px 2px rgba(255, 255, 255, 0.9);
  color: #555;
  font-size: 13px;
  white-space: nowrap;
  font-style: italic;
}

/* Zone List Control Panel */
.zone-list-control {
  margin-top: 10px;
  /* Position below zoom controls on left */
  margin-left: 10px;
  /* Add some spacing from edge */
}

.map-zone-list {
  background: white;
  border-radius: 4px;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.65);
  width: 300px;
  /* Slightly wider for better readability */
  max-height: 500px;
  /* Fixed height that works with 700px map */
  min-height: 200px;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  font-size: 14px;
  /* Increase base font size */
}

/* Collapsed icon state */
.map-zone-list-icon {
  background: white;
  border-radius: 4px;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.65);
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
}

.map-zone-list-icon:hover {
  background: #f8f9fa;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.map-zone-list-icon i {
  font-size: 16px;
  color: #333;
}

.map-zone-list-icon .zone-count-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  background: #007bff;
  color: white;
  border-radius: 10px;
  padding: 2px 6px;
  font-size: 11px;
  font-weight: bold;
  min-width: 18px;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.zone-list-header {
  padding: 12px;
  border-bottom: 1px solid #ddd;
  cursor: pointer;
  font-weight: bold;
  font-size: 15px;
  /* Larger header font */
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f8f9fa;
  border-radius: 4px 4px 0 0;
  user-select: none;
}

.zone-list-header:hover {
  background: #e9ecef;
}

.zone-list-header .fa {
  transition: transform 0.3s ease;
}

.zone-list-header .zone-count {
  margin-left: auto;
  font-weight: normal;
  color: #666;
  font-size: 13px;
}

.map-zone-list.collapsed {
  max-height: 40px;
  overflow: hidden;
}

.map-zone-list.collapsed .zone-list-header {
  border-bottom: none;
  border-radius: 4px;
}

.zone-list-content {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  /* Important for Firefox */
}

/* Adjust zone parts list styles when inside map */
.map-zone-list .zone-parts-list {
  padding: 0;
  border: none;
  background: transparent;
}

.map-zone-list .zone-parts-list .search-box {
  padding: 10px;
  border-bottom: 1px solid #eee;
}

.map-zone-list .zone-parts-list .search-box input {
  font-size: 14px;
}

.map-zone-list .zone-parts-list .zone-tree {
  padding: 10px;
}

.map-zone-list .zone-parts-list .zone-header {
  font-size: 14px;
  padding: 10px 12px;
}

.map-zone-list .zone-parts-list .zone-name {
  font-size: 14px;
  font-weight: 600;
}

.map-zone-list .zone-parts-list .zonepart-item {
  font-size: 13px;
  padding: 8px 12px;
}

.map-zone-list .zone-parts-list .zonepart-name {
  font-size: 13px;
}

.map-zone-list .zone-parts-list .list-actions {
  padding: 10px;
  border-top: 1px solid #eee;
  display: flex;
  gap: 5px;
}

.map-zone-list .zone-parts-list .list-actions button {
  font-size: 13px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .map-zone-list {
    width: 260px;
    max-height: 400px;
    /* Smaller fixed height for tablets */
    font-size: 13px;
  }

  .zone-list-header {
    font-size: 14px;
  }

  /* Responsive selected zonepart panel */
  .selected-zonepart-panel {
    min-width: 200px;
  }

  .selected-zonepart-panel .panel-header {
    padding: 8px 10px;
    font-size: 13px;
  }

  .selected-zonepart-panel .info-row {
    padding: 4px 0;
  }

  .selected-zonepart-panel .info-row label {
    font-size: 12px;
  }

  .selected-zonepart-panel .info-value {
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .map-zone-list {
    width: 220px;
    max-height: 300px;
    font-size: 12px;
  }

  .zone-list-header {
    font-size: 13px;
  }
}

/* Improve visibility of Leaflet.Editable vertex handles */
:deep(.leaflet-vertex-icon) {
  width: 1.5rem !important;
  height: 1.5rem !important;
  margin-left: -0.75rem !important;
  margin-top: -0.75rem !important;
  background: #fff !important;
  border: 3px solid #fe57a1 !important;
  border-radius: 50% !important;
  cursor: grab !important;
  z-index: 1000 !important;
}

:deep(.leaflet-vertex-icon:hover) {
  background: #fe57a1 !important;
  border-color: #fff !important;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.4), 0 0 0 3px rgba(254, 87, 161, 0.4) !important;
}

/* Middle markers (between vertices) */
:deep(.leaflet-middle-icon) {
  width: 1.5rem !important;
  height: 1.5rem !important;
  margin-left: -0.75rem !important;
  margin-top: -0.75rem !important;
  background: rgba(255, 255, 255, 0.7) !important;
  border: 3px solid #fe57a1 !important;
  border-radius: 50% !important;
  cursor: grab !important;
}

:deep(.leaflet-middle-icon:hover) {
  background: #fe57a1 !important;
  border-color: #fff !important;
  opacity: 1 !important;
}

/* Drawing cursor styles */
:deep(.leaflet-container.leaflet-editable-drawing) {
  cursor: crosshair !important;
}

/* Polygon editing styles - only animate stroke-width for hover effect */
:deep(.leaflet-interactive) {
  transition: stroke-width 0.2s ease;
}

/* Selected feature highlight */
.zonepart-layer {
  transition: opacity 0.3s ease;
}


/* Drag handle marker for Move tool */
:deep(.drag-handle-marker) {
  width: 40px !important;
  height: 40px !important;
  margin-left: -20px !important;
  margin-top: -20px !important;
  cursor: move !important;
  background-color: rgba(255, 102, 0, 0.85) !important;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='5 9 2 12 5 15'/%3E%3Cpolyline points='9 5 12 2 15 5'/%3E%3Cpolyline points='15 19 12 22 9 19'/%3E%3Cpolyline points='19 9 22 12 19 15'/%3E%3Cline x1='2' y1='12' x2='22' y2='12'/%3E%3Cline x1='12' y1='2' x2='12' y2='22'/%3E%3C/svg%3E") !important;
  background-repeat: no-repeat !important;
  background-position: center !important;
  background-size: 20px 20px !important;
  border: 2px solid #fff !important;
  border-radius: 50% !important;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3) !important;
}

/* Drawing tooltip */
:deep(.leaflet-editable-tooltip) {
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 4px 8px;
  border-radius: 3px;
  font-size: 12px;
  white-space: nowrap;
}

/* Move mode cursor */
:deep(.leaflet-dragging .leaflet-interactive) {
  cursor: grabbing !important;
}

/* Moveable polygon cursor */
:deep(.leaflet-pane .leaflet-interactive[style*="ff9900"]) {
  cursor: move !important;
}

:deep(.leaflet-pane .leaflet-interactive[style*="ff9900"]:active) {
  cursor: grabbing !important;
}

/* Fullscreen mode styles */
.service-area-map-container.is-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  z-index: 9999;
  background: #000;
  /* Black background to clearly show any gaps */
  /* Remove !important to allow proper cleanup on exit */
}

/* Map takes full height in fullscreen - handled by inline style now */
.service-area-map-container.is-fullscreen .leaflet-container {
  /* height and width are set via inline style based on isFullscreen flag */
}

/* Toolbar positioning in fullscreen */
.service-area-map-container.is-fullscreen .geometry-edit-toolbar {
  position: fixed !important;
  top: 10px !important;
  left: 50% !important;
  transform: translateX(-50%) !important;
  z-index: 10000 !important;
}

/* Zone list in fullscreen mode */
.service-area-map-container.is-fullscreen .map-zone-list {
  max-height: calc(100vh - 150px);
  /* Increased margin to prevent bottom cutoff */
}

/* Ensure Leaflet's fullscreen button works correctly */
:deep(.leaflet-control-fullscreen) {
  z-index: 1000 !important;
}

:deep(.leaflet-container-fullscreen) {
  z-index: 9999 !important;
}

/* Modal container for fullscreen support */
.map-modal-container {
  position: relative;
  background: transparent;
  /* Ensure no background blocks the map */
}

/* Modal overlay when in map context */
.modal-overlay.map-modal {
  position: absolute;
  z-index: 2000;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Edit name modal specific styles */
.edit-modal {
  background: white;
  padding: 20px;
  border-radius: 8px;
  min-width: 350px;
  max-width: 450px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.edit-modal h4 {
  margin-top: 0;
  margin-bottom: 15px;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.edit-modal .form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  margin-bottom: 15px;
}

.edit-modal .form-control:focus {
  outline: none;
  border-color: #3388ff;
  box-shadow: 0 0 0 3px rgba(51, 136, 255, 0.1);
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 15px;
}

.modal-actions .btn {
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.modal-actions .btn-primary {
  background: #3388ff;
  color: white;
}

.modal-actions .btn-primary:hover {
  background: #2c78e8;
}

.modal-actions .btn-default {
  background: #f5f5f5;
  color: #333;
  border: 1px solid #ddd;
}

.modal-actions .btn-default:hover {
  background: #e8e8e8;
}

/* Ensure modals appear correctly in fullscreen mode */
.service-area-map-container.is-fullscreen .map-modal-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10000;
  /* Ensure modals appear above map controls in fullscreen */
  pointer-events: none;
  background: transparent;
  /* Ensure no background blocks the map */
}

.service-area-map-container.is-fullscreen .map-modal-container>* {
  pointer-events: auto;
}

/* Normal mode - ensure proper positioning */
.service-area-map-container:not(.is-fullscreen) {
  position: relative;
  width: 100%;
  height: auto;
}

/* Normal mode - ensure modal container doesn't block interactions */
.service-area-map-container:not(.is-fullscreen) .map-modal-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 1000;
  /* Above map but below fixed modals */
}

.service-area-map-container:not(.is-fullscreen) .map-modal-container>* {
  pointer-events: auto;
}

/* Cursor styles based on active tool */
.service-area-map-container[data-active-tool="select"] .leaflet-interactive {
  cursor: pointer !important;
}

.service-area-map-container[data-active-tool="select"] .leaflet-interactive:hover {
  stroke-width: 4px !important;
}

.service-area-map-container[data-active-tool="new-sector"] {
  cursor: crosshair !important;
}

.service-area-map-container[data-active-tool="new-sector"] .leaflet-container {
  cursor: crosshair !important;
}

.service-area-map-container[data-active-tool="edit-shape"] .leaflet-container {
  cursor: default !important;
}

.service-area-map-container[data-active-tool="edit-shape"] .leaflet-interactive {
  cursor: grab !important;
}

.service-area-map-container[data-active-tool="move"] .leaflet-interactive {
  cursor: move !important;
}

.service-area-map-container[data-active-tool="delete"] .leaflet-interactive {
  cursor: not-allowed !important;
}

/* Location pins styling */
.location-pin-marker {
  /* Visual distinction from zone markers - makes pins blue */
  filter: hue-rotate(200deg);
  opacity: 0.9;
}

.location-pin-marker:hover {
  filter: hue-rotate(200deg) brightness(1.2);
  opacity: 1;
}

/* Location toggle button */
.location-toggle-btn {
  background: white;
  border: 2px solid rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  cursor: pointer;
  width: 34px;
  height: 34px;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  transition: background-color 0.2s;
}

.location-toggle-btn:hover {
  background-color: #f4f4f4;
}

.location-toggle-btn:active {
  background-color: #e0e0e0;
}

.location-toggle-btn i {
  margin: 0;
}

/* Location tooltip styling */
.location-tooltip {
  padding: 4px 8px;
  font-size: 12px;
  line-height: 1.4;
  min-width: 120px;
}

.location-tooltip strong {
  display: block;
  margin-bottom: 4px;
  color: #333;
  font-size: 13px;
}

.location-tooltip div {
  color: #666;
  margin-bottom: 2px;
}

.location-tooltip div:last-child {
  margin-bottom: 0;
}
</style>
